import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = [".open", "title", "subtitle"]
const _hoisted_2 = {
  id: "modal-container-element",
  class: "modal-content"
}
const _hoisted_3 = { slot: "footer" }
const _hoisted_4 = ["variant", "disabled"]
const _hoisted_5 = ["disabled"]

import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import AppSource from './AppSource.vue';
import AppBuildOptions from './AppBuildOptions.vue';
import AppInfo from './AppInfo.vue';
import AppConfiguration from './AppConfiguration.vue';
import AppProgress from './AppProgress.vue';
import Tabs from './Tabs.vue';
import { App } from '../../models/application/ui-types';
import { useAppForm } from '../../models/application/form/useAppForm';


export default /*@__PURE__*/_defineComponent({
  __name: 'AppModal',
  setup(__props, { expose: __expose }) {

const store = useStore() as any;
const t = store.getters['i18n/t'];

// Modal open state
const showModal = ref(false);
const modalMode = ref<'create' | 'edit'>('create');

const initialApp = ref<App | null>(null);

const { form, initialForm, populateFormFromApp, clearForm, state, update } = useAppForm(modalMode);

const activeTab = ref<string | number>('source')
const isPipelineActive = ref<boolean>(false);
const tabs = computed(() => [
  { id: 'source', 
    label: 'Source', 
    completed: (state.source.dirty.value || isEdit.value) && state.source.valid.value, 
    valid: state.source.valid.value, 
    disabled: isPipelineActive.value, 
    visible: true 
  },
  { id: 'build', 
    label: 'Build Options', 
    completed: (state.buildOptions.dirty.value || isEdit.value) && state.buildOptions.valid.value, 
    valid: state.buildOptions.valid.value, 
    disabled: isEdit.value ? isPipelineActive.value : !state.source.valid.value || isPipelineActive.value, 
    visible: true 
  },
  { id: 'details', 
    label: 'Details', 
    completed: (state.details.dirty.value || isEdit.value) && state.details.valid.value, 
    valid: state.details.valid.value, 
    disabled: isEdit.value ? isPipelineActive.value : !state.buildOptions.valid.value || isPipelineActive.value, 
    visible: true 
  },
  { id: 'bindings', 
    label: 'Bindings', 
    completed: (state.bindings.dirty.value || isEdit.value) && state.bindings.valid.value, 
    valid: state.bindings.valid.value, 
    disabled: isEdit.value ? isPipelineActive.value : !state.details.valid.value || isPipelineActive.value, 
    visible: true 
  },
  { id: 'progress', 
    label: 'Progress', 
    completed: false, 
    valid: true, 
    disabled: !state.dirty.value || !state.valid.value, 
    visible: true 
  }
])

const isEdit = computed(() => modalMode.value === 'edit');

const nextTab = computed(() => {
  const idx = tabs.value.findIndex(t => t.id === activeTab.value)
  return tabs.value[idx + 1]?.id
})

const prevTab = computed(() => {
  const idx = tabs.value.findIndex(t => t.id === activeTab.value)
  return tabs.value[idx - 1]?.id
})

const isDirty = computed(() => {
  return state.dirty.value;
});

const showDiscardConfirm = ref(false);

async function openCreate() {
  modalMode.value = 'create';
  showModal.value = true;
}

async function openEdit(row: App, commit?: string) {
  modalMode.value = 'edit';
  showModal.value = true;
  initialApp.value = row;
  const form = populateFormFromApp(row, true);

  if (commit) {
    update.source(form.source.type, { commit });
  }
}

function handleModalClose() {
  if (state.dirty.value) {
    showDiscardConfirm.value = true;
  } else {
    closeModal();
  }
}

function handleKeepEditing() {
  showDiscardConfirm.value = false;
}

function handleDiscard() {
  showDiscardConfirm.value = false;
  closeModal();
}

function closeModal() {
  clearForm();
  showDiscardConfirm.value = false;
  activeTab.value = 'source';
  showModal.value = false;
  modalMode.value = 'create';
  initialApp.value = null;
}

async function onSubmit() {
  if (isPipelineActive.value) {
    return;
  }

  completeTab('bindings', 'progress');
}

function completeTab(tabId: string | number, nextTabId: string | number) {
  activeTab.value = nextTabId
}

function onPipelineStart() {
  isPipelineActive.value = true;
}

function onPipelineSuccess() {
  isPipelineActive.value = false;
  closeModal();
}

function onPipelineFailure() {
  isPipelineActive.value = false;
}

function setModalToEdit() {
  modalMode.value = 'edit';
}

__expose({ openCreate, openEdit });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("trailhand-modal", {
    ".open": showModal.value,
    dismissible: false,
    title: (isEdit.value) ? (initialApp.value?.meta.name || 'Application') : 'Application',
    subtitle: (isEdit.value) ? (initialApp.value?.stateDisplay || 'Update') : 'Create New',
    position: "top",
    onModalClose: closeModal
  }, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(Tabs, {
        modelValue: activeTab.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event)),
        tabs: tabs.value
      }, {
        source: _withCtx(() => [
          _createVNode(AppSource, {
            source: _unref(form).source,
            mode: modalMode.value,
            updateSource: _unref(update).source,
            populateFormFromApp: _unref(populateFormFromApp)
          }, null, 8, ["source", "mode", "updateSource", "populateFormFromApp"])
        ]),
        build: _withCtx(({ tab }) => [
          _createVNode(AppBuildOptions, {
            buildOptions: _unref(form).buildOptions,
            sourceType: _unref(form).source?.type,
            modalOpen: showModal.value,
            mode: modalMode.value,
            active: activeTab.value === tab.id,
            updateBuildOptions: _unref(update).buildOptions
          }, null, 8, ["buildOptions", "sourceType", "modalOpen", "mode", "active", "updateBuildOptions"])
        ]),
        details: _withCtx(({ tab }) => [
          _createVNode(AppInfo, {
            details: _unref(form).details,
            mode: modalMode.value,
            source: _unref(form).source,
            chart: _unref(form).buildOptions.appChart,
            active: activeTab.value === tab.id,
            updateDetails: _unref(update).details,
            updateChartSettings: _unref(update).chartSettings,
            updateBindings: _unref(update).bindings
          }, null, 8, ["details", "mode", "source", "chart", "active", "updateDetails", "updateChartSettings", "updateBindings"])
        ]),
        bindings: _withCtx(() => [
          _createVNode(AppConfiguration, {
            bindings: _unref(form).bindings,
            namespace: _unref(form).details.namespace,
            mode: modalMode.value,
            active: activeTab.value === 'bindings',
            updateBindings: _unref(update).bindings
          }, null, 8, ["bindings", "namespace", "mode", "active", "updateBindings"])
        ]),
        progress: _withCtx(({ tab }) => [
          _createVNode(AppProgress, {
            form: _unref(form),
            initialForm: _unref(initialForm),
            initialApp: initialApp.value,
            formState: _unref(state),
            mode: modalMode.value,
            active: activeTab.value === tab.id,
            onPipelineStart: onPipelineStart,
            onPipelineSuccess: onPipelineSuccess,
            onPipelineFailure: onPipelineFailure,
            setModalToEdit: setModalToEdit
          }, null, 8, ["form", "initialForm", "initialApp", "formState", "mode", "active"])
        ]),
        _: 1,
        __: [3,4,5,6]
      }, 8, ["modelValue", "tabs"])
    ]),
    _cache[14] || (_cache[14] = _createTextVNode()),
    _createElementVNode("div", _hoisted_3, [
      (showDiscardConfirm.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "discard-message" }, "You have unsaved changes.", -1)),
            _cache[8] || (_cache[8] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "secondary",
              class: "mr-10",
              onButtonClick: handleKeepEditing
            }, "\n          Keep Editing\n        ", 32),
            _cache[9] || (_cache[9] = _createTextVNode()),
            _createElementVNode("trailhand-button", {
              variant: "destructive",
              onButtonClick: handleDiscard
            }, "\n          Discard\n        ", 32)
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (activeTab.value !== 'progress')
              ? (_openBlock(), _createElementBlock("trailhand-button", {
                  key: 0,
                  variant: "secondary",
                  class: "mr-10",
                  onButtonClick: handleModalClose
                }, "\n          Cancel\n        ", 32))
              : _createCommentVNode("", true),
            _cache[10] || (_cache[10] = _createTextVNode()),
            (!!prevTab.value && activeTab.value !== 'progress')
              ? (_openBlock(), _createElementBlock("trailhand-button", {
                  key: 1,
                  variant: "secondary",
                  class: "mr-10",
                  onButtonClick: _cache[1] || (_cache[1] = ($event: any) => (activeTab.value = prevTab.value))
                }, "\n          Previous\n        ", 32))
              : _createCommentVNode("", true),
            _cache[11] || (_cache[11] = _createTextVNode()),
            (nextTab.value && activeTab.value !== 'progress')
              ? (_openBlock(), _createElementBlock("trailhand-button", {
                  key: 2,
                  variant: !isEdit.value ? 'primary' : 'secondary',
                  class: "mr-10",
                  disabled: tabs.value.find(t => t.id === nextTab.value)?.disabled,
                  onButtonClick: _cache[2] || (_cache[2] = ($event: any) => (completeTab(activeTab.value, nextTab.value)))
                }, "\n          Next\n        ", 40, _hoisted_4))
              : _createCommentVNode("", true),
            _cache[12] || (_cache[12] = _createTextVNode()),
            (isEdit.value && activeTab.value !== 'progress')
              ? (_openBlock(), _createElementBlock("trailhand-button", {
                  key: 3,
                  variant: "primary",
                  disabled: !isDirty.value || tabs.value.some(t => !t.valid),
                  onButtonClick: onSubmit
                }, _toDisplayString(_unref(t)('generic.save')), 41, _hoisted_5))
              : _createCommentVNode("", true),
            _cache[13] || (_cache[13] = _createTextVNode()),
            (activeTab.value === 'progress')
              ? (_openBlock(), _createElementBlock("trailhand-button", {
                  key: 4,
                  variant: "primary",
                  disabled: false,
                  onButtonClick: closeModal
                }, "\n          Finish\n        ", 32))
              : _createCommentVNode("", true)
          ], 64))
    ])
  ], 40, _hoisted_1))
}
}

})